import api from './api';
import {
    SchoolAdminDashboardData,
    TeacherDashboardData,
    PendingMarksEntryPage
} from '../types/dashboardTypes';

interface ApiResponse<T> {
    success: boolean;
    message?: string;
    data: T;
}

/**
 * Dashboard API Service
 * Handles API calls for dashboard data
 */
export const dashboardService = {
    /**
     * Get School Admin Dashboard data
     * Single aggregate call for all dashboard widgets
     */
    getSchoolAdminDashboard: async (): Promise<ApiResponse<SchoolAdminDashboardData>> => {
        const response = await api.get<ApiResponse<SchoolAdminDashboardData>>(
            '/dashboard/school-admin'
        );
        return response.data as ApiResponse<SchoolAdminDashboardData>;
    },

    /**
     * Get Teacher Dashboard data
     * Returns teacher-specific widgets: attendance, leave balance, schedule, pending marks.
     * `pendingMarksEntry` carries only page 1 — additional pages are fetched
     * via `getTeacherPendingMarksPage` as the widget is scrolled.
     */
    getTeacherDashboard: async (): Promise<ApiResponse<TeacherDashboardData>> => {
        const response = await api.get<ApiResponse<TeacherDashboardData>>(
            '/dashboard/teacher'
        );
        return response.data as ApiResponse<TeacherDashboardData>;
    },

    /**
     * Fetch a specific page of the current teacher's pending-marks entries.
     * Used by the dashboard widget's infinite scroll.
     */
    getTeacherPendingMarksPage: async (
        page: number,
        pageSize?: number
    ): Promise<ApiResponse<PendingMarksEntryPage>> => {
        const response = await api.get<ApiResponse<PendingMarksEntryPage>>(
            '/dashboard/teacher/pending-marks',
            { params: { page, pageSize } }
        );
        return response.data as ApiResponse<PendingMarksEntryPage>;
    }
};

export default dashboardService;
